import { useEffect, useRef } from 'react';
import { FormInstance } from 'antd';

/**
 * The shared filter fields that every trigger node saves on its trigger object.
 * Node-specific fields (change_by, location, alert_types, …) are captured by
 * the generic parameter T so callers stay fully typed.
 */
export interface BaseTriggerData {
  courier_ids?: string[];
  team_ids?: string[];
  origin_ids?: string[];
  destination_ids?: string[];
  product_ids?: string[];
  select_all?: boolean;
  select_all_teams?: boolean;
  select_all_origins?: boolean;
  select_all_products?: boolean;
  select_all_transport_providers?: boolean;
  select_all_destinations?: boolean;
}

interface UseTriggerFormInitOptions<T extends BaseTriggerData> {
  /** Whether the customization side-panel is currently open. */
  isSidePannelOpen: boolean;
  /** The trigger-specific slice of nodeData (e.g. nodeData.eta_change_trigger). */
  triggerData: T | null | undefined;
  /** Ant Design form instance owned by the calling component. */
  form: FormInstance;
  /** Setter for the active apply-filter chips (transport=2, teams=5, …). */
  setApplyType: (types: number[]) => void;
  /**
   * Optional extra boolean gate. Default: true.
   * Use this when the calling component needs additional data before it can
   * patch (e.g. AlertTrigger waits for alertThreshold to be non-empty).
   * Because extraGuard is in the deps array, the effect automatically retries
   * when the gate flips false → true — no separate `deps` param needed.
   */
  extraGuard?: boolean;
  /**
   * Extra form fields to merge into setFieldsValues after the shared ones.
   * Use for node-specific fields like `change_by` or `location`.
   */
  extraFormValues?: Record<string, unknown>;
  /**
   * Called once, right after the shared fields are applied to the form.
   * Use for node-specific side-effects (e.g. building alertTypesObject for
   * AlertTrigger's setSelectedThresholdValues).
   */
  onPatched?: (data: T) => void;
}

/**
 * Initialises the shipment-filter form fields (and applyType chips) from
 * saved node data the first time the side-panel opens.
 *
 * All three trigger-node customization panels share identical logic for the
 * common filter fields. Component-specific fields are handled via
 * `extraFormValues` and `onPatched`.
 *
 * Returns `hasPatchedRef` in case the caller ever needs to reset it.
 */
export function useTriggerFormInit<T extends BaseTriggerData>({
  isSidePannelOpen,
  triggerData,
  form,
  setApplyType,
  extraGuard = true,
  extraFormValues,
  onPatched,
}: UseTriggerFormInitOptions<T>) {
  const hasPatchedRef = useRef(false);

  useEffect(() => {
    if (!isSidePannelOpen || hasPatchedRef.current) return;
    if (!triggerData) return;
    if (!extraGuard) return;
    hasPatchedRef.current = true;

    const {
      courier_ids: courierIds,
      team_ids: teamIds,
      origin_ids: originIds,
      destination_ids: destinationIds,
      product_ids: productIds,
      select_all: selectAll,
      select_all_teams: selectAllTeams,
      select_all_origins: selectAllOrigins,
      select_all_products: selectAllProducts,
      select_all_transport_providers: selectAllTransportProviders,
      select_all_destinations: selectAllDestinations,
    } = triggerData;

    let activeTypes: number[] = [];
    if (courierIds?.length || selectAllTransportProviders) activeTypes.push(2);
    if (teamIds?.length || selectAllTeams) activeTypes.push(5);
    if (originIds?.length || selectAllOrigins) activeTypes.push(3);
    if (destinationIds?.length || selectAllDestinations) activeTypes.push(4);
    if (productIds?.length || selectAllProducts) activeTypes.push(1);
    if (selectAll) activeTypes = [3, 4, 5, 2, 1];
    setApplyType(activeTypes);

    form.setFieldsValue({
      filter_transport_providers: courierIds ?? [],
      filter_teams: teamIds ?? [],
      filter_origins: originIds ?? [],
      filter_destinations: destinationIds ?? [],
      products: productIds ?? [],
      select_all_teams: selectAllTeams ?? false,
      select_all_origins: selectAllOrigins ?? false,
      select_all_products: selectAllProducts ?? false,
      select_all_transport_providers: selectAllTransportProviders ?? false,
      select_all_destinations: selectAllDestinations ?? false,
      ...extraFormValues,
    });

    onPatched?.(triggerData);
    // extraFormValues and onPatched are intentionally excluded: they are inline
    // objects/functions that change reference every render, but hasPatchedRef
    // ensures this effect only runs once. extraGuard covers the AlertTrigger
    // "wait for threshold data" use-case, making a separate deps param unneeded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSidePannelOpen, triggerData, form, extraGuard, setApplyType]);

  return hasPatchedRef;
}
